import {
  IconButton,
  TypographyVariantsEnum,
  Counter,
  IconFilters,
  Typography,
  IconSort,
  IconHeart,
  Divider,
  ModalSheet,
  ListBoxItem,
  ListBox,
  ListBoxSelectionType,
  getSortKey,
  SortKey,
  getSortFromKey,
  Skeleton_v2 as Skeleton,
} from '@mindly/ui-components';
import { useCallback, useMemo, useState } from 'react';
import { PrivateRoutes } from 'src/routes/navigation';
import { useAnalytics } from 'src/shared';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';
import { ImpactStyle } from '@capacitor/haptics';
import { useHistory } from 'react-router-dom';
import {
  specialistsFiltersCountSelector,
  specialistsGoalsSelector,
  specialistsSortKeySelector,
  specilistTotalSelector,
} from 'src/redux/specialists/selector';
import { useDispatch, useSelector } from 'react-redux';
import { favouriteSpecialistsListSelector } from 'src/redux/favouriteSpecialits/selector';
import { setSort } from 'src/redux/specialists/reducer';
import { Selection } from '@react-types/shared';
import { fetchSpecialistsList } from 'src/redux/specialists/thunk';
import { saveSort } from 'src/shared/utils/filters';
import { useTranslation } from 'react-i18next';

type Props = {
  isLoading: boolean;
};

const SpecialistListControlsFeature: React.FC<Props> = ({ isLoading }) => {
  const { trackPredefinedEvent } = useAnalytics();
  const history = useHistory();
  const filtersCount = useSelector(specialistsFiltersCountSelector);
  const favouriteSpecialistList = useSelector(favouriteSpecialistsListSelector);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortKey = useSelector(specialistsSortKeySelector);
  const dispatch = useDispatch();
  const { t } = useTranslation('ui');
  const goals = useSelector(specialistsGoalsSelector);
  const hasGoals = useMemo(() => goals?.length > 0, [goals]);
  const specialistsTotal = useSelector(specilistTotalSelector);

  const handleClickFiltersButton = useCallback(async () => {
    isomorficHapticsImpact({ style: ImpactStyle.Light });
    trackPredefinedEvent('SPECIALIST_LIST_FILTER_BUTTON_CLICKED');
    history.push(PrivateRoutes.PSYCHOLOGIST_LIST_FILTERS);
  }, [history, trackPredefinedEvent]);

  const handleFavouriteButtonClick = useCallback(async () => {
    isomorficHapticsImpact({ style: ImpactStyle.Light });
    history.push(PrivateRoutes.FAVOURITE_SPECIALISTS_TAB);
  }, []);

  const handleCloseSort = useCallback(() => {
    setIsSortOpen(false);
  }, []);

  const handleOpenSort = useCallback(() => {
    setIsSortOpen(true);
  }, []);

  const handleSortChange = useCallback((value: Selection) => {
    if (value instanceof Set) {
      const sort = getSortFromKey(value.values().next().value as SortKey);
      dispatch(setSort(sort));
      saveSort(sort);
      dispatch(fetchSpecialistsList('')).unwrap();
      handleCloseSort();
    }
  }, []);

  return (
    <>
      {isLoading ? (
        <div className="px-5 pb-4 pt-3 flex flex-col gap-1">
          <Skeleton className="w-full h-7 rounded-md" />
          <Skeleton className="w-1/2 h-5 rounded-md" />
        </div>
      ) : hasGoals || specialistsTotal > 0 ? (
        <div className="px-5 pb-4 pt-3 flex flex-col gap-1">
          <Typography variant={TypographyVariantsEnum.Title20Semi} className="line-clamp-2">
            {hasGoals ? goals.join(', ') : t('ui:filters.results')}
          </Typography>
          {specialistsTotal > 0 && (
            <Typography variant={TypographyVariantsEnum.Text15Semi} className="text-typography-typography-secondary">
              {t('ui:filters.total_specialists', { count: specialistsTotal })}
            </Typography>
          )}
        </div>
      ) : null}
      <Divider />
      <div className="sticky top-0 bg-color-background-white z-30">
        <div className="px-5 py-4 flex justify-between">
          <Typography
            variant={TypographyVariantsEnum.Text14Semi}
            className="flex items-center gap-1 cursor-pointer relative"
            onClick={handleClickFiltersButton}
          >
            <div className="relative">
              <IconButton icon={<IconFilters fill="none" />} />
              {filtersCount > 0 && <Counter className="absolute -top-0.5 -right-1">{filtersCount}</Counter>}
            </div>
            {t('filters.title')}
          </Typography>
          {specialistsTotal > 0 && (
            <Typography
              variant={TypographyVariantsEnum.Text14Semi}
              className="flex items-center gap-1 cursor-pointer"
              onClick={handleOpenSort}
            >
              <IconButton icon={<IconSort />} />
              {t('sort.label')}
            </Typography>
          )}
          <Typography
            variant={TypographyVariantsEnum.Text14Semi}
            className="flex items-center gap-1 cursor-pointer"
            onClick={handleFavouriteButtonClick}
          >
            <div className="relative">
              <IconButton icon={<IconHeart />} />
              {favouriteSpecialistList && favouriteSpecialistList?.length > 0 && (
                <Counter className="absolute -top-0.5 -right-1">{favouriteSpecialistList?.length}</Counter>
              )}
            </div>
            {t('favourite.title')}
          </Typography>
        </div>
        <Divider />
        <ModalSheet isOpen={isSortOpen} onDidDismiss={handleCloseSort} className="[--height:auto]">
          <div className="pb-[calc(var(--ion-safe-area-bottom)_+_theme(spacing.4))] px-5 pt-10">
            <Typography variant={TypographyVariantsEnum.Title24Semi} className="text-center mb-4">
              {t('sort.title')}
            </Typography>
            <ListBox
              selectionMode="single"
              type={ListBoxSelectionType.Radio}
              selectedKeys={new Set([sortKey])}
              onSelectionChange={handleSortChange}
            >
              <ListBoxItem key={getSortKey('is_new', 'desc')} textValue={getSortKey('is_new', 'desc')} divider>
                {t('sort.is_new.desc')}
              </ListBoxItem>
              {/* <ListBoxItem
              key={getSortKey('near_the_slot', 'asc')}
              textValue={getSortKey('near_the_slot', 'asc')}
              divider
            >
              {t('sort.nearest_slot.asc')}
            </ListBoxItem> */}
              <ListBoxItem
                key={getSortKey('session_price', 'asc')}
                textValue={getSortKey('session_price', 'asc')}
                divider
              >
                {t('sort.price.asc')}
              </ListBoxItem>
              <ListBoxItem
                key={getSortKey('session_price', 'desc')}
                textValue={getSortKey('session_price', 'desc')}
                divider
              >
                {t('sort.price.desc')}
              </ListBoxItem>
              <ListBoxItem
                key={getSortKey('reviews_average_rating', 'desc')}
                textValue={getSortKey('reviews_average_rating', 'desc')}
                divider
              >
                {t('sort.reviews_average_rating.desc')}
              </ListBoxItem>
              <ListBoxItem key={getSortKey('reviews_count', 'desc')} textValue={getSortKey('reviews_count', 'desc')}>
                {t('sort.reviews_count.desc')}
              </ListBoxItem>
            </ListBox>
          </div>
        </ModalSheet>
      </div>
    </>
  );
};

export default SpecialistListControlsFeature;
